// ════════════════════════════════════════════════════════════════════════════
// FECHAMENTO DE CICLO — Sessão 31 (29/09/2026 — Walber)
// Tela só EXIBE o que o motor devolve (app/utils/quadro_demonstrativo.py).
// Nenhuma conta aqui. A linha TOTAL do Condensado vem pronta do backend.
// Única escrita: "Confirmar" um aviso (POST .../confirmar-alerta, exige login).
// ════════════════════════════════════════════════════════════════════════════
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  Box, Card, CardContent, Typography,
  Chip, Button, Alert, Collapse, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Snackbar, Tooltip, GlobalStyles,
} from '@mui/material';
import { Summarize, Print, KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { API_URL } from './config';
import { useAuth } from './AuthContext';
import ModuleHeader from './components/ModuleHeader';
import BotaoArquivar from './BotaoArquivar';
import CondensadoPapel from './CondensadoPapel';
import FechamentoCabecalho, { BarraDaAba, AreasBotoes, ehSemRG, estadoDaArea, textoRascunho, totalComDatas } from './FechamentoCabecalho';

// ─── Tipos (espelho do motor) ────────────────────────────────────────────────
type Nivel = 'bloqueio' | 'confirmar' | 'info';
type Situacao = 'bloqueada' | 'precisa_de_voce' | 'em_andamento' | 'pronta';
type Valor = number | string | null;
interface Confirmacao { por: string | null; em: string | null; decisao: string | null; }
interface Alerta {
  codigo: string; nivel: Nivel; titulo: string; explicacao: string;
  o_que_fazer: string; confirmado: Confirmacao | null;
}
interface ComFonte { valor: Valor; fonte: string; }
interface Diferenca { campo: string; papel: any; tovis: any; }
interface Linha {
  area_id: number; area: string; ciclo_id: number; categoria: string | null; origem: string;
  situacao: Situacao; pode_imprimir: boolean; status_area: 'ABERTA' | 'FECHADA';
  sem: string | null; inicio: string | null; termino: string | null; amostras: Valor;
  qrt_exist: ComFonte; qrt_trab: Valor; qrt_pos: Valor; imov_exist: ComFonte;
  trabalhados: Valor; informados: Valor; imov_insp: Valor; imov_trat: Valor; imov_pos: Valor;
  pend_total: Valor; pend_pct: Valor; iip_ant: Valor; iip: Valor; ib_ant: Valor; ib: Valor;
  larvicida_g: Valor; larv_casa: Valor; dep_insp: Valor; dep_trat: Valor; dep_elim: Valor;
  dep_pos: Valor; hd: Valor; rend: Valor; positivos_fonte: string;
  pe_insp?: Valor; pe_trat?: Valor; pe_pos?: Valor;
  alertas: Alerta[]; diferencas_tovis_papel?: Diferenca[]; observacao: string | null;
}
interface Quadro {
  ciclo: { id: number; nome?: string; ano?: number; numero?: number };
  linhas: Linha[];
  resumo: { total_areas: number; por_situacao: Record<Situacao, number>; pode_imprimir_todas: boolean };
}
interface Condensado {
  cabecalho: { municipio: string; atividade: string; ano: number; ciclo_numero: number; modalidade?: string };
  colunas: string[]; linhas: Record<string, Valor>[]; total: Record<string, Valor>;
  consolidado?: Record<string, Valor>;
  pode_imprimir_todas: boolean; areas_em_andamento: string[];
  bloqueios: { area: string; motivo: string; nivel: Nivel }[];
}
interface Ciclo { id: number; nome: string; ativo?: boolean; ano?: number; numero?: number; }

// ─── Situação: cores SÓ do tema MUI ──────────────────────────────────────────
const SIT: Record<Situacao, { cor: 'error' | 'warning' | 'info' | 'success'; emoji: string; rotulo: string }> = {
  bloqueada:       { cor: 'error',   emoji: '🔴', rotulo: 'Bloqueada' },
  precisa_de_voce: { cor: 'warning', emoji: '🟡', rotulo: 'Precisa de você' },
  em_andamento:    { cor: 'info',    emoji: '🔵', rotulo: 'Em andamento' },
  pronta:          { cor: 'success', emoji: '🟢', rotulo: 'Pronta' },
};
const ORDEM_SIT: Situacao[] = ['bloqueada', 'precisa_de_voce', 'em_andamento', 'pronta'];

function rotuloSituacao(l: Linha): string {
  if (l.situacao !== 'em_andamento') return SIT[l.situacao].rotulo;
  if (ehSemRG(l)) return 'Falta cadastrar o RG da área';
  return l.status_area === 'ABERTA'
    ? 'Em andamento — área aberta no Fechamento de Área'
    : 'Em andamento — quarteirões do RG sem conclusão';
}

// ─── Formatação (sem conta: só texto) ────────────────────────────────────────
const ddmm = (iso: string | null) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}` : '');
const mostra = (v: Valor | undefined) => (v === null || v === undefined || v === '' ? '—' : String(v));
/** "3416" -> "3416,0"; "12,34" -> "12,3". Corta o texto, nunca arredonda. */
function umaCasa(v: Valor | undefined): string {
  if (v === null || v === undefined || v === '') return '—';
  const s = String(v).replace('.', ',');
  const [i, d = ''] = s.split(',');
  return `${i},${(d + '0').slice(0, 1)}`;
}
function fmtQuando(s: string | null): string {
  if (!s) return '';
  if (s.length <= 10) return `${s.slice(8, 10)}/${s.slice(5, 7)}/${s.slice(0, 4)}`;
  const d = new Date(s);
  return isNaN(d.getTime()) ? s : d.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}
const FONTE_TXT: Record<string, string> = {
  RG: 'RG', digitado: '✍️ digitado', 'cadastro de áreas': 'cadastro de áreas', papel: '📄 papel', falta: 'falta',
};
const SUGESTAO_INFORMADOS =
  'Sobra dentro da tolerância; origem nos boletins em papel (Auditoria de Fechamento). Aceito pelo supervisor.';
const CHAVE_RETORNO_AUDITORIA = 'tovis_fechamento_retorno'; // lida pelo FechamentoArea.tsx

function motivosQueTravam(alertas: Alerta[]): string {
  return alertas
    .filter(a => a.nivel === 'bloqueio' || (a.nivel === 'confirmar' && !a.confirmado))
    .map(a => a.titulo).join('; ');
}

// ─── Pequenos componentes ────────────────────────────────────────────────────
function Bolinha({ s }: { s: Situacao }) {
  return <Box component="span" sx={{
    display: 'inline-block', width: 12, height: 12, borderRadius: '50%',
    bgcolor: `${SIT[s].cor}.main`, flexShrink: 0,
  }} />;
}

function SituacaoTag({ l }: { l: Linha }) {
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
      <Bolinha s={l.situacao} />
      <Typography component="span" sx={{ fontWeight: 700, fontSize: 14, color: `${SIT[l.situacao].cor}.dark` }}>
        {rotuloSituacao(l)}
      </Typography>
    </Box>
  );
}

function Tarja({ texto }: { texto: string }) {
  return (
    <Box className="fc-tarja" sx={{
      border: '2px dashed', borderColor: 'info.main', color: 'info.dark', textAlign: 'center',
      fontWeight: 800, letterSpacing: 2, py: 0.5, mb: 1, fontSize: 13,
    }}>{texto}</Box>
  );
}

// ─── CSS da folha e da impressão ─────────────────────────────────────────────
const ESTILO_FOLHA = `
  .fc-tabela { border-collapse: collapse; width: 100%; font-family: Arial, Helvetica, sans-serif; font-size: 11px; color: #000; }
  .fc-tabela th, .fc-tabela td { border: 1px solid #000; padding: 3px 4px; text-align: center; vertical-align: middle; }
  .fc-tabela th { background: #e8e8e8; font-weight: 700; font-size: 10px; }
  .fc-tabela td.fc-esq, .fc-tabela th.fc-esq { text-align: left; }
  .fc-tabela tr.fc-total td { font-weight: 800; background: #f4f4f4; }
  .fc-tabela tr.fc-branco td { height: 18px; }
  .fc-calc { text-decoration: underline dotted; text-underline-offset: 3px; }
  .fc-cab { font-family: Arial, Helvetica, sans-serif; color: #000; }
  .fc-printonly { display: none; }
  .fc-tabela td.fc-idx, .fc-tabela td.fc-pos { color: #c00000; }
  @media print {
    @page { size: A4 landscape; margin: 7mm; }
    html, body { background: #fff !important; }
    .fc-noprint, .no-print { display: none !important; }
    .fc-pagina { background: #fff !important; padding: 0 !important; min-height: 0 !important; }
    .fc-folha { box-shadow: none !important; border: none !important; padding: 0 !important; margin: 0 !important; }
    .fc-scroll { overflow: visible !important; }
    .fc-tabela { font-size: 8.5px; }
    .fc-tabela th { font-size: 7.5px; background: #e8e8e8 !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .fc-tabela th, .fc-tabela td { padding: 2px 2px; }
    .fc-idx, .fc-pos { color: #c00000 !important; }
    .fc-calc { text-decoration: none !important; }
    .fc-tarja { border-color: #000 !important; color: #000 !important; }
    .fc-printonly { display: block !important; }
  }
`;

// ════════════════════════════════════════════════════════════════════════════
export default function FechamentoCiclo({ onVoltar, onNavegar }: { onVoltar: () => void; onNavegar: (p: string) => void }) {
  const { token } = useAuth();
  const [ciclos, setCiclos] = useState<Ciclo[]>([]);
  const [cicloId, setCicloId] = useState<number | ''>('');
  const [aba, setAba] = useState(0);
  const [quadro, setQuadro] = useState<Quadro | null>(null);
  const [cond, setCond] = useState<Condensado | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');
  const [areaFolha, setAreaFolha] = useState<number | ''>('');
  const [aviso, setAviso] = useState('');
  const seq = useRef(0);

  // Diálogo "Confirmar"
  const [dlg, setDlg] = useState<{ linha: Linha; alerta: Alerta } | null>(null);
  const [motivo, setMotivo] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [erroDlg, setErroDlg] = useState('');

  const headers = token ? { Authorization: `Bearer ${token}` } : undefined;

  useEffect(() => {
    axios.get(`${API_URL}/api/ciclos/`, { headers })
      .then(r => {
        const lista: Ciclo[] = Array.isArray(r.data) ? r.data : [];
        setCiclos(lista);
        const ativo = lista.find(c => c.ativo) ?? lista[0];
        if (ativo) setCicloId(ativo.id);
      })
      .catch(() => setErro('Não foi possível carregar a lista de ciclos.'));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const carregar = useCallback(async () => {
    if (!cicloId) return;
    const meu = ++seq.current;
    setCarregando(true); setErro('');
    try {
      const [q, c] = await Promise.all([
        axios.get<Quadro>(`${API_URL}/api/fechamento-ciclo/${cicloId}/quadro`, { headers }),
        axios.get<Condensado>(`${API_URL}/api/fechamento-ciclo/${cicloId}/condensado`, { headers }),
      ]);
      if (meu !== seq.current) return;
      setQuadro(q.data); setCond(c.data);
      setAreaFolha(prev => (prev && q.data.linhas.some(l => l.area_id === prev)) ? prev : (q.data.linhas[0]?.area_id ?? ''));
    } catch {
      if (meu === seq.current) { setQuadro(null); setCond(null); setErro('Não foi possível carregar o Quadro deste ciclo.'); }
    } finally {
      if (meu === seq.current) setCarregando(false);
    }
  }, [cicloId, token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { carregar(); }, [carregar]);

  const cicloSel = ciclos.find(c => c.id === cicloId);
  const cicloNumero = quadro?.ciclo.numero ?? cicloSel?.numero;
  const cicloAno = quadro?.ciclo.ano ?? cicloSel?.ano;
  // Cabeçalho novo (07/10/2026): resumo das áreas e áreas sem RG, só com o que o motor já mandou
  const areasResumo = (quadro?.linhas ?? []).map(l => ({ id: l.area_id, nome: l.area, estado: estadoDaArea(l), rotulo: rotuloSituacao(l) }));
  const areasSemRG = (quadro?.linhas ?? []).filter(ehSemRG).map(l => l.area);

  // ── Confirmar aviso ─────────────────────────────────────────────────────
  const abrirConfirmar = (linha: Linha, alerta: Alerta) => {
    setDlg({ linha, alerta });
    setMotivo(alerta.codigo === 'INFORMADOS_MAIOR_QUE_EXIST' ? SUGESTAO_INFORMADOS : '');
    setErroDlg('');
  };
  const enviarConfirmacao = async () => {
    if (!dlg || !cicloId) return;
    setEnviando(true); setErroDlg('');
    try {
      await axios.post(
        `${API_URL}/api/fechamento-ciclo/${cicloId}/area/${dlg.linha.area_id}/confirmar-alerta`,
        { alerta: dlg.alerta.codigo, decisao: motivo.trim() }, { headers },
      );
      setAviso(`✅ Aviso confirmado em ${dlg.linha.area}.`);
      setDlg(null);
      await carregar();
    } catch (e: any) {
      const st = e?.response?.status;
      const det = e?.response?.data?.detail;
      if (st === 401) setErroDlg('Sua sessão expirou. Saia e entre de novo no sistema.');
      else if (typeof det === 'string') setErroDlg(det);
      else if (Array.isArray(det)) setErroDlg(det.map((x: any) => x.msg).join('; '));
      else setErroDlg('Não foi possível confirmar. Tente de novo.');
    } finally { setEnviando(false); }
  };

  const abrirAuditoria = (areaId: number) => {
    try {
      if (cicloId) sessionStorage.setItem(CHAVE_RETORNO_AUDITORIA, JSON.stringify({ areaId, cicloId }));
    } catch { /* sem armazenamento: abre sem área selecionada */ }
    onNavegar('fechamento_area');
  };

  const verFolha = (areaId: number) => { setAreaFolha(areaId); setAba(1); window.scrollTo(0, 0); };

  const motivoOk = motivo.trim().length >= 10;

  return (
    <Box className="fc-pagina" sx={{ minHeight: '100vh', bgcolor: 'grey.50' }}>
      <GlobalStyles styles={ESTILO_FOLHA} />

      <Box className="fc-noprint">
        <ModuleHeader
          titulo="Fechamento de Ciclo"
          subtitulo="Quadro Demonstrativo por área e Condensado para Amargosa"
          icone={<Summarize fontSize="inherit" />}
          accentColor="#0097A7"
          onVoltar={onVoltar}
        />
      </Box>

      <Box sx={{ px: { xs: 2, md: 4 }, pb: 6, maxWidth: 1400, mx: 'auto' }}>
        {/* Cabeçalho: ciclo + resumo das áreas + abas em trilha (FechamentoCabecalho.tsx, 07/10/2026) */}
        <FechamentoCabecalho
          ciclos={ciclos} cicloId={cicloId} onCiclo={setCicloId} carregando={carregando}
          aba={aba} onAba={setAba} areas={areasResumo}
        />

        {erro && <Alert severity="error" className="fc-noprint" sx={{ mb: 2 }}>{erro}</Alert>}

        {/* Abas sem botões próprios (Painel, TDAH-friendly e Instruções): só o cartão "onde estou" */}
        {(aba === 0 || aba === 3 || aba === 5) && <BarraDaAba aba={aba} />}
        {aba === 0 && quadro && (
          <AbaPainel quadro={quadro} onConfirmar={abrirConfirmar} onAuditoria={abrirAuditoria} onVerFolha={verFolha} />
        )}
        {aba === 1 && quadro && (
          <AbaFolha quadro={quadro} areaId={areaFolha} setAreaId={setAreaFolha}
                    cicloNumero={cicloNumero} cicloAno={cicloAno} />
        )}
        {aba === 2 && cond && <AbaCondensado cond={cond} semRG={areasSemRG} cicloId={cicloId} nomeCiclo={cicloSel?.nome ?? (cicloNumero ? `Ciclo ${cicloNumero}/${cicloAno ?? ''}` : `Ciclo ${cicloId}`)} />}
        {/* Aba 5: Consolidado Digitação — o município inteiro em uma linha (07/10/2026). */}
        {aba === 4 && cond && <AbaConsolidado cond={cond} semRG={areasSemRG} cicloId={cicloId} nomeCiclo={cicloSel?.nome ?? (cicloNumero ? `Ciclo ${cicloNumero}/${cicloAno ?? ''}` : `Ciclo ${cicloId}`)} />}
        {/* Aba 4: o mesmo Condensado no modo "passar para o papel" (CondensadoPapel.tsx, 02/10/2026). Mesmos textos da aba 3. */}
        {aba === 3 && cond && (
          <CondensadoPapel
            titulos={COLS_COND.map(c => c.t)}
            linhas={cond.linhas.map(row => COLS_COND.map(c => (c.umaCasa ? umaCasa(row[c.k]) : mostra(row[c.k]))))}
            total={COLS_COND.map(c => {
              const v = totalComDatas(cond.total, cond.consolidado)[c.k];
              if (v === null || v === undefined) return '';
              return c.umaCasa ? umaCasa(v) : mostra(v);
            })}
            municipio={cond.cabecalho.municipio}
            atividade={cond.cabecalho.atividade}
            ano={cond.cabecalho.ano}
            nomeCiclo={cicloSel?.nome ?? (cicloNumero ? `Ciclo ${cicloNumero}/${cicloAno ?? ''}` : `Ciclo ${cicloId}`)}
            cicloId={cicloId}
            emAndamento={cond.areas_em_andamento}
            bloqueios={cond.pode_imprimir_todas ? [] : cond.bloqueios}
            situacoes={(quadro?.linhas ?? []).map(l => ({
              area: l.area,
              semRG: l.imov_exist.fonte !== 'RG',
              fechada: l.status_area === 'FECHADA',
              positivosPapel: l.positivos_fonte === 'papel',
              linhaPapel: l.origem === 'papel',
            }))}
          />
        )}
        {aba === 5 && <AbaInstrucoes />}
      </Box>

      {/* Diálogo Confirmar */}
      <Dialog open={!!dlg} onClose={() => !enviando && setDlg(null)} maxWidth="sm" fullWidth className="fc-noprint">
        <DialogTitle sx={{ fontWeight: 800 }}>✅ Confirmar aviso — {dlg?.linha.area}</DialogTitle>
        <DialogContent>
          {dlg && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography sx={{ fontWeight: 800 }}>{dlg.alerta.titulo}</Typography>
              <Typography variant="body2">{dlg.alerta.explicacao}</Typography>
            </Alert>
          )}
          <TextField
            label="Motivo / decisão" value={motivo} onChange={e => setMotivo(e.target.value)}
            multiline minRows={3} fullWidth autoFocus
            error={motivo.length > 0 && !motivoOk}
            helperText={motivoOk ? 'Fica gravado com seu nome, data e hora.' : 'Obrigatório — mínimo 10 caracteres.'}
          />
          {erroDlg && <Alert severity="error" sx={{ mt: 2, whiteSpace: 'pre-line' }}>{erroDlg}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDlg(null)} disabled={enviando}>Cancelar</Button>
          <Button variant="contained" color="success" onClick={() => enviarConfirmacao()} disabled={!motivoOk || enviando}>
            {enviando ? 'Gravando…' : 'Confirmar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!aviso} autoHideDuration={4000} onClose={() => setAviso('')}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="success" variant="filled" onClose={() => setAviso('')}>{aviso}</Alert>
      </Snackbar>
    </Box>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// ABA 1 — PAINEL DO CICLO
// ════════════════════════════════════════════════════════════════════════════
function AbaPainel({ quadro, onConfirmar, onAuditoria, onVerFolha }: {
  quadro: Quadro;
  onConfirmar: (l: Linha, a: Alerta) => void;
  onAuditoria: (areaId: number) => void;
  onVerFolha: (areaId: number) => void;
}) {
  const cont = quadro.resumo.por_situacao;
  return (
    <Box className="fc-noprint">
      {/* Faixa de resumo */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2,1fr)', md: 'repeat(4,1fr)' }, gap: 1.5, mb: 3 }}>
        {ORDEM_SIT.map(s => (
          <Card key={s} sx={{ borderRadius: 3, borderTop: 4, borderColor: `${SIT[s].cor}.main` }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography sx={{ fontSize: 30, fontWeight: 800, color: `${SIT[s].cor}.dark`, lineHeight: 1.1 }}>
                {cont?.[s] ?? 0}
              </Typography>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'text.secondary' }}>
                {SIT[s].emoji} {SIT[s].rotulo}{s === 'bloqueada' || s === 'pronta' ? 's' : ''}
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Box>

      {quadro.linhas.length === 0 && <Alert severity="info">Nenhuma área com boletins neste ciclo.</Alert>}

      {quadro.linhas.map(l => <CartaoArea key={l.area_id} l={l}
        onConfirmar={onConfirmar} onAuditoria={onAuditoria} onVerFolha={onVerFolha} />)}

      {/* Envelope */}
      <Card sx={{ borderRadius: 3, mt: 3, border: 1, borderColor: 'divider' }} variant="outlined">
        <CardContent>
          <Typography sx={{ fontWeight: 800, fontSize: 16, mb: 1 }}>✉️ O que vai no envelope para Amargosa</Typography>
          <Box component="ol" sx={{ m: 0, pl: 3, '& li': { mb: 0.5 } }}>
            <li>Resumo do Reconhecimento Geográfico de cada área</li>
            <li>FAD-07 semanais do ciclo (1 por semana)</li>
            <li>FAD-07 Geral de cada área</li>
            <li>Todos os FAD-07 de Ponto Estratégico</li>
          </Box>
          <Typography sx={{ mt: 1, fontWeight: 700 }}>📎 Itens 1, 2 e 3 <u>GRAMPEADOS</u>.</Typography>
          <Typography sx={{ mt: 1 }}>Envelope → <b>Aninha</b> → <b>Amargosa</b>.</Typography>
          <Typography>O <b>Consolidado Digitação</b> (aba 5) → <b>Paulinho</b> digita.</Typography>
        </CardContent>
      </Card>
    </Box>
  );
}

function CartaoArea({ l, onConfirmar, onAuditoria, onVerFolha }: {
  l: Linha;
  onConfirmar: (l: Linha, a: Alerta) => void;
  onAuditoria: (areaId: number) => void;
  onVerFolha: (areaId: number) => void;
}) {
  const [verDif, setVerDif] = useState(false);
  const papel = l.origem === 'papel';
  const numeros: [string, string][] = [
    ['Período', `${ddmm(l.inicio)}–${ddmm(l.termino)}`],
    ['Qrt TRAB/EXIST', `${mostra(l.qrt_trab)}/${mostra(l.qrt_exist.valor)}`],
    ['IIP', mostra(l.iip)],
    ['Breteau', mostra(l.ib)],
    ['Pendência %', mostra(l.pend_pct)],
  ];
  return (
    <Card sx={{ borderRadius: 3, mb: 2, borderLeft: 6, borderColor: `${SIT[l.situacao].cor}.main` }}>
      <CardContent>
        {/* Linha 1: nome + situação + ação */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 1 }}>
          <Typography sx={{ fontWeight: 800, fontSize: 18 }}>{l.area}</Typography>
          <SituacaoTag l={l} />
          <Box sx={{ flex: 1 }} />
          <Button size="small" variant="outlined" onClick={() => onVerFolha(l.area_id)}>Ver folha</Button>
        </Box>

        {/* Linha 2: números */}
        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 1 }}>
          {numeros.map(([r, v]) => (
            <Box key={r}>
              <Typography sx={{ fontSize: 11, color: 'text.secondary', textTransform: 'uppercase', fontWeight: 700 }}>{r}</Typography>
              <Typography sx={{ fontSize: 16, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{v}</Typography>
            </Box>
          ))}
        </Box>

        {/* Linha 3: fontes */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {papel && <Chip size="small" label="📄 Linha do papel" color="default" variant="outlined" sx={{ fontWeight: 700 }} />}
          <Chip size="small" variant="outlined"
                label={`Positivos: ${l.positivos_fonte === 'papel' ? '📄 papel' : 'TOVIS'}`} />
          {!papel && l.qrt_exist.fonte !== 'RG' &&
            <Chip size="small" variant="outlined" label={`Qrt existentes: ${FONTE_TXT[l.qrt_exist.fonte] ?? l.qrt_exist.fonte}`} />}
          {!papel && l.imov_exist.fonte !== 'RG' &&
            <Chip size="small" variant="outlined" label={`Imóveis existentes: ${FONTE_TXT[l.imov_exist.fonte] ?? l.imov_exist.fonte}`} />}
        </Box>

        {/* Avisos */}
        {l.alertas.length > 0 && (
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
            {l.alertas.map(a => <CaixaAviso key={a.codigo} l={l} a={a} onConfirmar={onConfirmar} onAuditoria={onAuditoria} />)}
          </Box>
        )}

        {/* Diferenças papel × boletins */}
        {papel && (l.diferencas_tovis_papel?.length ?? 0) > 0 && (
          <Box sx={{ mt: 1.5 }}>
            <Button size="small" onClick={() => setVerDif(!verDif)}
                    endIcon={verDif ? <KeyboardArrowUp /> : <KeyboardArrowDown />}>
              Ver diferenças papel × boletins ({l.diferencas_tovis_papel!.length})
            </Button>
            <Collapse in={verDif}>
              <Typography sx={{ fontSize: 12, color: 'text.secondary', my: 0.5 }}>
                Só informativo: a linha usada é a do papel.
              </Typography>
              <Box component="table" className="fc-tabela" sx={{ maxWidth: 520 }}>
                <thead><tr><th className="fc-esq">Campo</th><th>Papel</th><th>Boletins (TOVIS)</th></tr></thead>
                <tbody>
                  {l.diferencas_tovis_papel!.map(d => (
                    <tr key={d.campo}>
                      <td className="fc-esq">{d.campo}</td>
                      <td>{fmtDif(d.papel)}</td>
                      <td>{fmtDif(d.tovis)}</td>
                    </tr>
                  ))}
                </tbody>
              </Box>
            </Collapse>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

function fmtDif(v: any): string {
  if (v && typeof v === 'object') return `${mostra(v.valor)} (${FONTE_TXT[v.fonte] ?? v.fonte})`;
  return mostra(v);
}

function CaixaAviso({ l, a, onConfirmar, onAuditoria }: {
  l: Linha; a: Alerta;
  onConfirmar: (l: Linha, a: Alerta) => void;
  onAuditoria: (areaId: number) => void;
}) {
  if (a.confirmado) {
    return (
      <Box sx={{ bgcolor: 'grey.100', color: 'text.secondary', borderRadius: 2, p: 1.5 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 14 }}>{a.titulo}</Typography>
        <Typography sx={{ fontSize: 13 }}>
          ✅ Confirmado por {a.confirmado.por ?? '—'} em {fmtQuando(a.confirmado.em)}
          {a.confirmado.decisao ? ` — ${a.confirmado.decisao}` : ''}
        </Typography>
      </Box>
    );
  }
  const sev = a.nivel === 'bloqueio' ? 'error' : a.nivel === 'confirmar' ? 'warning' : 'info';
  return (
    <Alert severity={sev} sx={{ borderRadius: 2 }}>
      <Typography sx={{ fontWeight: 800, fontSize: 14 }}>{a.titulo}</Typography>
      <Typography sx={{ fontSize: 13 }}>{a.explicacao}</Typography>
      <Typography sx={{ fontSize: 13, mt: 0.5 }}><b>O que fazer:</b> {a.o_que_fazer}</Typography>
      {(a.nivel === 'confirmar' || a.codigo === 'INFORMADOS_MAIOR_QUE_EXIST') && (
        <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
          {a.nivel === 'confirmar' &&
            <Button size="small" variant="contained" color="success" onClick={() => onConfirmar(l, a)}>✅ Confirmar</Button>}
          {a.codigo === 'INFORMADOS_MAIOR_QUE_EXIST' &&
            <Button size="small" variant="outlined" onClick={() => onAuditoria(l.area_id)}>🔍 Abrir Auditoria</Button>}
        </Box>
      )}
    </Alert>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// ABA 2 — QUADRO POR ÁREA (folha igual ao papel)
// ════════════════════════════════════════════════════════════════════════════
interface ColunaFolha {
  grupo: string; sub?: string; idx?: boolean; pos?: boolean; calc?: boolean;
  valor: (l: Linha) => string;
  dica?: (l: Linha) => string;
}
const dicaPos = (l: Linha) => `positivos: ${l.positivos_fonte === 'papel' ? '📄 papel' : 'TOVIS'}`;
const COLS_FOLHA: ColunaFolha[] = [
  { grupo: 'Nº SEM', valor: l => mostra(l.sem) },
  { grupo: 'P.E.', sub: 'INSP', valor: l => mostra(l.pe_insp), dica: () => 'boletins de Ponto Estratégico: soma de Inspecionados' },
  { grupo: 'P.E.', sub: 'TRAT', valor: l => mostra(l.pe_trat), dica: () => 'boletins de Ponto Estratégico: soma de Trat. Focal' },
  { grupo: 'P.E.', sub: 'POST', pos: true, valor: l => mostra(l.pe_pos), dica: () => 'boletins de Ponto Estratégico: imóveis positivos digitados no boletim' },
  { grupo: 'CAT', valor: l => mostra(l.categoria) },
  { grupo: 'TOTAL DE AMOSTRAS', valor: l => mostra(l.amostras) },
  { grupo: 'DATA', sub: 'INÍCIO', valor: l => ddmm(l.inicio) },
  { grupo: 'DATA', sub: 'TÉRMINO', valor: l => ddmm(l.termino) },
  { grupo: 'QUARTEIRÕES', sub: 'EXIST.', valor: l => mostra(l.qrt_exist.valor), dica: l => `existentes: ${FONTE_TXT[l.qrt_exist.fonte] ?? l.qrt_exist.fonte}` },
  { grupo: 'QUARTEIRÕES', sub: 'TRAB.', valor: l => mostra(l.qrt_trab), dica: () => 'quarteirões concluídos nos boletins' },
  { grupo: 'QUARTEIRÕES', sub: 'POS.', valor: l => mostra(l.qrt_pos), dica: dicaPos },
  { grupo: 'IMÓVEIS', sub: 'EXIST.', valor: l => mostra(l.imov_exist.valor), dica: l => `existentes: ${FONTE_TXT[l.imov_exist.fonte] ?? l.imov_exist.fonte}` },
  { grupo: 'IMÓVEIS', sub: 'INSP.', valor: l => mostra(l.imov_insp) },
  { grupo: 'IMÓVEIS', sub: 'TRAT.', valor: l => mostra(l.imov_trat) },
  { grupo: 'IMÓVEIS', sub: 'POS.', pos: true, valor: l => mostra(l.imov_pos), dica: dicaPos },
  { grupo: 'PENDÊNCIA', sub: 'TOTAL', valor: l => mostra(l.pend_total), dica: () => 'fechados + recusas − recuperados' },
  { grupo: 'PENDÊNCIA', sub: '%', idx: true, valor: l => mostra(l.pend_pct), dica: () => 'calculado: pendência × 100 ÷ (trabalhados + pendência)' },
  { grupo: 'I.I. PREDIAL', sub: 'ANT.', idx: true, valor: l => mostra(l.iip_ant), dica: () => 'ATUAL do ciclo anterior' },
  { grupo: 'I.I. PREDIAL', sub: 'ATUAL', idx: true, valor: l => mostra(l.iip), dica: l => `calculado: imóveis positivos × 100 ÷ inspecionados (${dicaPos(l)})` },
  { grupo: 'ÍNDICE DE BRETOR', sub: 'ANT.', idx: true, valor: l => mostra(l.ib_ant), dica: () => 'ATUAL do ciclo anterior' },
  { grupo: 'ÍNDICE DE BRETOR', sub: 'ATUAL', idx: true, valor: l => mostra(l.ib), dica: l => `calculado: depósitos positivos × 100 ÷ inspecionados (${dicaPos(l)})` },
  { grupo: 'BTI GASTO MÉDIA', sub: 'POR QUILO', valor: l => umaCasa(l.larvicida_g), dica: () => 'larvicida em gramas' },
  { grupo: 'BTI GASTO MÉDIA', sub: 'POR CASA', calc: true, valor: l => mostra(l.larv_casa), dica: () => 'calculado: gramas ÷ imóveis tratados' },
  { grupo: 'DEPÓSITOS', sub: 'INSP', valor: l => mostra(l.dep_insp) },
  { grupo: 'DEPÓSITOS', sub: 'TRAT', valor: l => mostra(l.dep_trat), dica: () => 'depósitos tratados com larvicida' },
  { grupo: 'DEPÓSITOS', sub: 'POS.', pos: true, valor: l => mostra(l.dep_pos), dica: dicaPos },
  { grupo: 'HOMEM DIA PROGRAMADO (L.I.)', valor: l => mostra(l.hd), dica: () => 'agente + dia distintos' },
  { grupo: 'RENDIMENTO H/DIA PROG. (TRAT.)', calc: true, valor: l => mostra(l.rend), dica: () => 'calculado: trabalhados ÷ homem-dia' },
  { grupo: 'TOTAL DE AGENTES', sub: 'L.I.', valor: () => '' },
  { grupo: 'TOTAL DE AGENTES', sub: 'TRAT.', valor: () => '' },
];

/** Agrupa colunas consecutivas do mesmo grupo para o cabeçalho de 2 níveis. */
function gruposCabecalho(cols: ColunaFolha[]) {
  const out: { grupo: string; span: number; temSub: boolean }[] = [];
  cols.forEach(c => {
    const ult = out[out.length - 1];
    if (ult && ult.grupo === c.grupo && c.sub) ult.span += 1;
    else out.push({ grupo: c.grupo, span: 1, temSub: !!c.sub });
  });
  return out;
}

function CabecalhoSMS() {
  return (
    <Box className="fc-cab" sx={{ textAlign: 'center', fontSize: 11, fontWeight: 700, lineHeight: 1.35, mb: 1 }}>
      <div>SECRETARIA MUNICIPAL DE SAÚDE DE MILAGRES</div>
      <div>DIRETORIA DE VIGILÂNCIA EPIDEMIOLÓGICA - DIVEP</div>
      <div>29ª DIRETORIA REGIONAL DE SAÚDE</div>
      <div>PCFAD - PROGRAMA DE CONTROLE DE FEBRE AMARELA E DENGUE</div>
    </Box>
  );
}

function AbaFolha({ quadro, areaId, setAreaId, cicloNumero, cicloAno }: {
  quadro: Quadro; areaId: number | ''; setAreaId: (v: number) => void;
  cicloNumero?: number; cicloAno?: number;
}) {
  const l = quadro.linhas.find(x => x.area_id === areaId);
  const grupos = gruposCabecalho(COLS_FOLHA);
  const papel = l?.origem === 'papel';
  // Arquivos: dados da folha da área para o botão "Arquivar"
  const nomeCicloFolha = quadro.ciclo.nome ?? (cicloNumero ? `Ciclo ${cicloNumero}/${cicloAno ?? ''}` : `Ciclo ${quadro.ciclo.id}`);
  const pedidoFolha = l ? {
    pasta: 'fechamento_ciclo' as const, tipo: 'quadro_area', chave: `fc_quadro|c${quadro.ciclo.id}|a${areaId}`,
    titulo: `Quadro Demonstrativo · ${l.area} · ${nomeCicloFolha}`, ciclo_id: Number(quadro.ciclo.id), area_id: Number(areaId),
    referencia: nomeCicloFolha, avisos: l.status_area === 'ABERTA' ? ['Área ainda aberta: a cópia sai com a marca RASCUNHO.'] : [],
  } : null;
  return (
    <Box>
      <BarraDaAba
        aba={1}
        extra={(
          <AreasBotoes
            valor={areaId}
            onEscolher={setAreaId}
            areas={quadro.linhas.map(x => ({
              id: x.area_id, nome: x.area, estado: estadoDaArea(x), rotulo: rotuloSituacao(x),
              nota: x.status_area === 'ABERTA' ? 'Enquanto a área estiver aberta, a folha sai com a marca RASCUNHO.' : (x.pode_imprimir ? 'Pode imprimir e arquivar.' : ''),
            }))}
          />
        )}
      >
        {l && (
          <Button variant="contained" startIcon={<Print />} disabled={!l.pode_imprimir} onClick={() => window.print()}>
            Imprimir
          </Button>
        )}
        {l && <BotaoArquivar alvo={() => document.querySelector('.fc-folha')} pedido={pedidoFolha} disabled={!l.pode_imprimir} />}
      </BarraDaAba>
      {l && !l.pode_imprimir && (
        <Alert severity="warning" className="fc-noprint" sx={{ mb: 2 }}>
          Esta folha não imprime: <b>{motivosQueTravam(l.alertas)}</b> — resolva no Painel do Ciclo.
        </Alert>
      )}
      {l && (
        <Typography className="fc-noprint" sx={{ fontSize: 12, color: 'text.secondary', mb: 1 }}>
          Números com <span className="fc-calc">sublinhado pontilhado</span> são calculados pelo sistema.
          Passe o mouse sobre um número para ver a fonte.
        </Typography>
      )}

      {l && (
        <Card className="fc-folha" sx={{ borderRadius: 2, p: 2 }}>
          {l.status_area === 'ABERTA' && <Tarja texto={ehSemRG(l) ? 'RASCUNHO — falta cadastrar o RG da área' : 'RASCUNHO — área em andamento'} />}
          <CabecalhoSMS />
          <Typography className="fc-cab" sx={{ textAlign: 'center', fontWeight: 800, fontSize: 14, mb: 1 }}>
            QUADRO DEMONSTRATIVO DAS OPERAÇÕES DE CAMPO
          </Typography>
          <Box className="fc-cab" sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', fontSize: 12, mb: 1 }}>
            <span><b>MUNICÍPIO:</b> Milagres</span>
            <span><b>LOCALIDADE:</b> {l.area}</span>
            <span><b>CAT:</b> {l.categoria ?? '—'}</span>
            <span><b>CICLO:</b> {cicloNumero ? `${cicloNumero}º` : '—'}/{cicloAno ?? ''}</span>
            <span><b>ANO:</b> {cicloAno ?? ''}</span>
            <span><b>ATIVIDADE:</b> LI+T</span>
          </Box>
          <Box className="fc-scroll" sx={{ overflowX: 'auto' }}>
            <table className="fc-tabela">
              <thead>
                <tr>
                  {grupos.map((g, i) => (
                    <th key={i} colSpan={g.span} rowSpan={g.temSub ? 1 : 2}>{g.grupo}</th>
                  ))}
                </tr>
                <tr>
                  {COLS_FOLHA.filter(c => c.sub).map((c, i) => <th key={i}>{c.sub}</th>)}
                </tr>
              </thead>
              <tbody>
                <tr>
                  {COLS_FOLHA.map((c, i) => {
                    const v = c.valor(l);
                    const dica = papel ? `linha do papel${c.dica ? ' · ' + c.dica(l) : ''}` : c.dica?.(l);
                    const cls = [c.idx ? 'fc-idx' : '', c.pos ? 'fc-pos' : '', (c.idx || c.calc) && v && v !== '—' ? 'fc-calc' : ''].join(' ').trim();
                    return (
                      <td key={i} className={cls || undefined}>
                        {dica && v ? (
                          <Tooltip title={dica} arrow><span>{v}</span></Tooltip>
                        ) : v}
                      </td>
                    );
                  })}
                </tr>
                {Array.from({ length: 10 }).map((_, r) => (
                  <tr key={`b${r}`} className="fc-branco">
                    {COLS_FOLHA.map((_, i) => <td key={i}>&nbsp;</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
          <Box className="fc-cab" sx={{ display: 'flex', gap: 6, mt: 3, fontSize: 12 }}>
            <span>RESP. PELAS INFORMAÇÕES: ______________________________</span>
            <span>DATA: ___/___/_____</span>
          </Box>
        </Card>
      )}
    </Box>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// ABA 3 — CONDENSADO (AMARGOSA)
// ════════════════════════════════════════════════════════════════════════════
const COLS_COND: { k: string; t: string; idx?: boolean; pos?: boolean; esq?: boolean; umaCasa?: boolean }[] = [
  { k: 'localidade', t: 'LOCALIDADE', esq: true }, { k: 'cat', t: 'CAT' }, { k: 'ciclo', t: 'CICLO' },
  { k: 'inic', t: 'INIC' }, { k: 'term', t: 'TERM' }, { k: 'imov_exist', t: 'IMOV EXIST' },
  { k: 'imov_trab', t: 'IMOV TRAB' }, { k: 'imov_insp', t: 'IMOV INSP' }, { k: 'imov_trat', t: 'IMOV TRAT' },
  { k: 'pe_trab', t: 'P.E TRAB' }, { k: 'imov_pend', t: 'IMOV PEND' }, { k: 'imov_pos', t: 'IMOV POS', pos: true },
  { k: 'dep_insp', t: 'DEP INSP' }, { k: 'dep_trat', t: 'DEP TRAT' }, { k: 'dep_elim', t: 'DEP ELIM' },
  { k: 'dep_pos', t: 'DEP POS', pos: true }, { k: 'iip', t: 'IIP %', idx: true }, { k: 'ind_pend', t: 'IND PEND %', idx: true },
  { k: 'hdt', t: 'H.D.T.' }, { k: 'rend', t: 'REND H/DIA' },
  { k: 'pyrip', t: 'BTI', umaCasa: true }, { k: 'pyrip_casa', t: 'BTI GASTO P/ IMOV GRS.' },
];

function AbaCondensado({ cond, semRG, cicloId, nomeCiclo }: { cond: Condensado; semRG: string[]; cicloId: number | ''; nomeCiclo: string }) {
  // Arquivos: dados do Condensado para o botão "Arquivar"
  const pedidoArquivo = cicloId ? {
    pasta: 'fechamento_ciclo' as const, tipo: 'condensado', chave: `fc_condensado|c${cicloId}`,
    titulo: `Condensado (Amargosa) · ${nomeCiclo}`, ciclo_id: Number(cicloId), referencia: nomeCiclo,
    avisos: cond.areas_em_andamento.length ? [`Área(s) ainda aberta(s): ${cond.areas_em_andamento.join(', ')}. A cópia sai com a marca RASCUNHO.`] : [],
  } : null;
  const celula = (row: Record<string, Valor>, c: typeof COLS_COND[number], total: boolean) => {
    const v = row[c.k];
    if (total && (v === null || v === undefined)) return '';
    return c.umaCasa ? umaCasa(v) : mostra(v);
  };
  return (
    <Box>
      <BarraDaAba aba={2}>
        <Button variant="contained" startIcon={<Print />} disabled={!cond.pode_imprimir_todas} onClick={() => window.print()}>
          Imprimir
        </Button>
        <BotaoArquivar alvo={() => document.querySelector('.fc-folha')} pedido={pedidoArquivo} disabled={!cond.pode_imprimir_todas} />
      </BarraDaAba>
      {!cond.pode_imprimir_todas && (
        <Alert severity="warning" className="fc-noprint" sx={{ mb: 2 }}>
          <Typography sx={{ fontWeight: 800, fontSize: 14 }}>O Condensado ainda não imprime. Resolva no Painel do Ciclo:</Typography>
          <Box component="ul" sx={{ m: 0, pl: 3 }}>
            {cond.bloqueios.map((b, i) => (
              <li key={i}><b>{b.area}</b> — {b.motivo} ({b.nivel === 'bloqueio' ? 'bloqueio' : 'confirmar'})</li>
            ))}
          </Box>
        </Alert>
      )}

      <Card className="fc-folha" sx={{ borderRadius: 2, p: 2 }}>
        {cond.areas_em_andamento.length > 0 && (
          <Tarja texto={textoRascunho(cond.areas_em_andamento, semRG)} />
        )}
        <Box className="fc-cab" sx={{ textAlign: 'center', fontSize: 11, fontWeight: 700, lineHeight: 1.35, mb: 1 }}>
          <div>PROGRAMA DE COMBATE AS ENDEMIAS</div>
          <div>SECRETARIA MUNICIPAL DE SAUDE - AMARGOSA</div>
          <div>COORDENAÇÃO DE ENDEMIAS</div>
          <div>PROGRAMA DE COMBATE A FEBRE AMARELA E DENGUE</div>
        </Box>
        <Typography className="fc-cab" sx={{ textAlign: 'center', fontWeight: 800, fontSize: 14, mb: 1 }}>
          QUADRO DEMONSTRATIVO DAS OPERAÇÕES DE CAMPO
        </Typography>
        <Box className="fc-cab" sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', fontSize: 12, mb: 1 }}>
          <span><b>Município:</b> {cond.cabecalho.municipio}</span>
          <span><b>Atividade:</b> {cond.cabecalho.atividade}</span>
          <span><b>Ano:</b> {cond.cabecalho.ano}</span>
        </Box>
        <Box className="fc-scroll" sx={{ overflowX: 'auto' }}>
          <table className="fc-tabela">
            <thead>
              <tr>{COLS_COND.map(c => <th key={c.k} className={c.esq ? 'fc-esq' : undefined}>{c.t}</th>)}</tr>
            </thead>
            <tbody>
              {cond.linhas.map((row, i) => (
                <tr key={i}>
                  {COLS_COND.map(c => (
                    <td key={c.k} className={[c.idx ? 'fc-idx' : '', c.pos ? 'fc-pos' : '', c.esq ? 'fc-esq' : ''].join(' ').trim() || undefined}>
                      {celula(row, c, false)}
                    </td>
                  ))}
                </tr>
              ))}
              <tr className="fc-total">
                {COLS_COND.map(c => (
                  <td key={c.k} className={[c.idx ? 'fc-idx' : '', c.pos ? 'fc-pos' : '', c.esq ? 'fc-esq' : ''].join(' ').trim() || undefined}>
                    {celula(totalComDatas(cond.total, cond.consolidado), c, true)}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </Box>
        <Typography className="fc-noprint" sx={{ fontSize: 12, color: 'text.secondary', mt: 1 }}>
          TOTAL: contagens somadas; índices calculados sobre os totais do município, com casas decimais cortadas.
        </Typography>
      </Card>
    </Box>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// ABA 5 — CONSOLIDADO DIGITAÇÃO (07/10/2026): a mesma folha do Condensado com UMA linha, o município
// inteiro no ciclo. A linha vem pronta do motor (cond.consolidado = linha TOTAL + identificação).
// ════════════════════════════════════════════════════════════════════════════
function AbaConsolidado({ cond, semRG, cicloId, nomeCiclo }: { cond: Condensado; semRG: string[]; cicloId: number | ''; nomeCiclo: string }) {
  const linha = cond.consolidado;
  // Arquivos: dados do Consolidado para o botão "Arquivar"
  const pedidoArquivo = cicloId ? {
    pasta: 'fechamento_ciclo' as const, tipo: 'consolidado', chave: `fc_consolidado|c${cicloId}`,
    titulo: `Consolidado Digitação · ${nomeCiclo}`, ciclo_id: Number(cicloId), referencia: nomeCiclo,
    avisos: cond.areas_em_andamento.length ? [`Área(s) ainda aberta(s): ${cond.areas_em_andamento.join(', ')}. A cópia sai com a marca RASCUNHO.`] : [],
  } : null;
  if (!linha) {
    return <Alert severity="info">A linha do município ainda não chegou do servidor. Atualize a página (F5).</Alert>;
  }
  const celula = (c: typeof COLS_COND[number]) => {
    const v = linha[c.k];
    if (v === null || v === undefined) return '';
    return c.umaCasa ? umaCasa(v) : mostra(v);
  };
  return (
    <Box>
      <BarraDaAba aba={4}>
        <Button variant="contained" startIcon={<Print />} disabled={!cond.pode_imprimir_todas} onClick={() => window.print()}>
          Imprimir
        </Button>
        <BotaoArquivar alvo={() => document.querySelector('.fc-folha')} pedido={pedidoArquivo} disabled={!cond.pode_imprimir_todas} />
      </BarraDaAba>
      {!cond.pode_imprimir_todas && (
        <Alert severity="warning" className="fc-noprint" sx={{ mb: 2 }}>
          <Typography sx={{ fontWeight: 800, fontSize: 14 }}>O Consolidado ainda não imprime. Resolva no Painel do Ciclo:</Typography>
          <Box component="ul" sx={{ m: 0, pl: 3 }}>
            {cond.bloqueios.map((b, i) => (
              <li key={i}><b>{b.area}</b> — {b.motivo} ({b.nivel === 'bloqueio' ? 'bloqueio' : 'confirmar'})</li>
            ))}
          </Box>
        </Alert>
      )}

      <Card className="fc-folha" sx={{ borderRadius: 2, p: 2 }}>
        {cond.areas_em_andamento.length > 0 && (
          <Tarja texto={textoRascunho(cond.areas_em_andamento, semRG)} />
        )}
        <Box className="fc-cab" sx={{ textAlign: 'center', fontSize: 11, fontWeight: 700, lineHeight: 1.35, mb: 1 }}>
          <div>PROGRAMA DE COMBATE AS ENDEMIAS</div>
          <div>SECRETARIA MUNICIPAL DE SAUDE - AMARGOSA</div>
          <div>COORDENAÇÃO DE ENDEMIAS</div>
          <div>PROGRAMA DE COMBATE A FEBRE AMARELA E DENGUE</div>
        </Box>
        <Typography className="fc-cab" sx={{ textAlign: 'center', fontWeight: 800, fontSize: 14, mb: 1 }}>
          QUADRO DEMONSTRATIVO DAS OPERAÇÕES DE CAMPO
        </Typography>
        <Box className="fc-cab" sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', fontSize: 12, mb: 1 }}>
          <span><b>Município:</b> {cond.cabecalho.municipio}</span>
          <span><b>Atividade:</b> {cond.cabecalho.atividade}{cond.cabecalho.modalidade ? ` — ${cond.cabecalho.modalidade}` : ''}</span>
          <span><b>Ano:</b> {cond.cabecalho.ano}</span>
        </Box>
        <Box className="fc-scroll" sx={{ overflowX: 'auto' }}>
          <table className="fc-tabela">
            <thead>
              <tr>{COLS_COND.map(c => <th key={c.k} className={c.esq ? 'fc-esq' : undefined}>{c.t}</th>)}</tr>
            </thead>
            <tbody>
              <tr className="fc-total">
                {COLS_COND.map(c => (
                  <td key={c.k} className={[c.idx ? 'fc-idx' : '', c.pos ? 'fc-pos' : '', c.esq ? 'fc-esq' : ''].join(' ').trim() || undefined}>
                    {celula(c)}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </Box>
        <Typography className="fc-noprint" sx={{ fontSize: 12, color: 'text.secondary', mt: 1 }}>
          Linha do município: mesmos números da linha TOTAL do Condensado (aba 3). Início = o primeiro início entre as áreas;
          término = o último término. Em vermelho: positivos e índices, como no papel.
        </Typography>
      </Card>
    </Box>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// ABA 6 — INSTRUÇÕES
// ════════════════════════════════════════════════════════════════════════════
function Bloco({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <Card sx={{ borderRadius: 3, mb: 2 }}>
      <CardContent>
        <Typography sx={{ fontWeight: 800, fontSize: 17, mb: 1 }}>■ {titulo}</Typography>
        <Box sx={{ fontSize: 14, lineHeight: 1.6, '& ul, & ol': { m: 0, pl: 3 }, '& li': { mb: 0.5 } }}>{children}</Box>
      </CardContent>
    </Card>
  );
}

const FORMULAS: [string, string][] = [
  ['IIP', 'imóveis positivos × 100 ÷ imóveis inspecionados'],
  ['Breteau', 'depósitos positivos × 100 ÷ imóveis inspecionados'],
  ['Pendência', 'fechados + recusas − recuperados'],
  ['Pendência %', 'pendência × 100 ÷ (trabalhados + pendência)'],
  ['Larvicida por casa', 'gramas ÷ imóveis tratados'],
  ['Rendimento', 'trabalhados ÷ homem-dia'],
  ['ANT', 'ATUAL do ciclo anterior'],
  ['Casas decimais', 'CORTADAS, nunca arredondadas'],
  ['Positivos', 'aegypti + albopictus (sem tipo PE, sem "outros")'],
  ['Quarteirões positivos', 'soma das semanas'],
  ['Quarteirões TRAB', 'concluídos'],
  ['Depósitos TRAT', 'depósitos tratados com larvicida'],
];

function AbaInstrucoes() {
  return (
    <Box className="fc-noprint" sx={{ maxWidth: 900 }}>
      <Bloco titulo="O que é">
        Fechar o ciclo = <b>conferir cada área</b>, preencher o <b>Quadro Demonstrativo</b> de cada área,
        montar o <b>envelope para Amargosa</b> e entregar o <b>Condensado</b> para digitação.
      </Bloco>
      <Bloco titulo="Antes de fechar (checklist)">
        <ol>
          <li>Todos os <b>quarteirões concluídos</b>.</li>
          <li><b>Pendências recuperadas</b> dentro do ciclo.</li>
          <li><b>Resultados de laboratório</b> lançados <b>OU</b> Quadro de Positividade em papel conferido.</li>
          <li><b>FAD-07 semanais</b> prontos e sem faixa vermelha.</li>
          <li>Área <b>fechada no Fechamento de Área</b> (rodar a Auditoria antes).</li>
        </ol>
      </Bloco>
      <Bloco titulo="Passo 1 — Quadro por Área">
        A cada área fechada, conferir a folha na <b>aba 2</b> e copiar/imprimir.
        Área aberta = <b>RASCUNHO</b>.
      </Bloco>
      <Bloco titulo="Passo 2 — Envelope">
        Resumo do RG de cada área + FAD-07 semanais do ciclo + FAD-07 Geral de cada área
        (os três <b>GRAMPEADOS</b>) + todos os FAD-07 de PE → <b>Aninha</b> → <b>Amargosa</b>.
      </Bloco>
      <Bloco titulo="Passo 3 — Condensado">
        Conferir a <b>aba 3</b> (uma linha por área + total) e passar para o papel.
        Entregar a <b>aba 5</b> (o município inteiro em uma linha) ao <b>Paulinho</b> para digitar.
      </Bloco>
      <Bloco titulo="Como o sistema calcula">
        <table className="fc-tabela" style={{ maxWidth: 720 }}>
          <tbody>
            {FORMULAS.map(([k, v]) => (
              <tr key={k}><td className="fc-esq" style={{ fontWeight: 700, width: 190 }}>{k}</td><td className="fc-esq">{v}</td></tr>
            ))}
          </tbody>
        </table>
      </Bloco>
      <Bloco titulo="As cores">
        <ul>
          <li>🔴 <b>Bloqueada</b> = falta um dado, não imprime.</li>
          <li>🟡 <b>Precisa de você</b> = um aviso para confirmar.</li>
          <li>🔵 <b>Em andamento</b> = área ainda aberta (imprime como RASCUNHO).</li>
          <li>🟢 <b>Pronta</b>.</li>
        </ul>
      </Bloco>
      <Bloco titulo="Os avisos mais comuns">
        <ul>
          <li><b>Positivos sem fonte</b> — amostras sem resultado → digitar os positivos do Quadro de Positividade em papel.</li>
          <li><b>Mais quarteirões trabalhados que existentes</b> — quarteirão declarado duas vezes.</li>
          <li><b>Mais imóveis informados que existentes</b> — sobra; até 8 é tolerado se a origem for explicada — abrir a Auditoria.</li>
          <li><b>Imóveis positivos &gt; depósitos positivos</b> — o SISPNCD recusa; corrigir.</li>
        </ul>
      </Bloco>
    </Box>
  );
}
